import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { IconLibrary, iconLibrary } from '@finnairoyj/fcom-ui-styles';

import { UiTranslateModule } from '@fcom/ui-translate/ui-translate.module';

import { toKebabCase } from '../../../utils/string.utils';
import { type IconName } from '../icons.interfaces';
import { GenericIconComponent } from '../generic-icon/generic-icon.component';

@Component({
  selector: 'fcom-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-testid]': 'dataTestIdValue()',
  },
  imports: [GenericIconComponent, UiTranslateModule],
})
export class IconComponent {
  category = input<IconLibrary>(IconLibrary.SVG_LIBRARY);
  dataTestId = input<string>();
  name = input<IconName>(null);
  // when this is set to true, make sure the translations are available in cms
  informative = input(false);

  private errorThrown = false;
  private iconLibrary: typeof iconLibrary = iconLibrary;

  private effectiveCategory = computed((): IconLibrary => this.category() ?? IconLibrary.SVG_LIBRARY);

  /** @default category-name-icon-name */
  dataTestIdValue = computed(
    () => this.dataTestId() || toKebabCase(`${this.effectiveCategory()}-${this.name() ?? 'unknown'}`)
  );

  iconRef = computed(() => `${this.effectiveCategory()}/${this.name()}`);

  svgPath = computed(() => {
    if (this.iconLibrary?.[this.effectiveCategory()]?.[this.name()]) {
      return this.iconLibrary[this.effectiveCategory()][this.name()];
    }

    this.throwSingleError(`Invalid Icon: ${String(this.effectiveCategory())}/${this.name()}`);
    return '';
  });

  // Throw an error in a timeout to stop it crashing other components but still report to sentry.
  throwSingleError(err: string): void {
    if (this.errorThrown) {
      return;
    }

    this.errorThrown = true;

    setTimeout(() => {
      throw new Error(err);
    }, 0);
  }
}
